// =============================================================================
// hooks/sales/useContactPreferences.ts — Leitura e mutação de preferências de
// contato (`contact_preferences`, F34-S04, doc 26 §8/§22).
//
// Endpoints (apps/api/src/modules/sales/preferences/routes.ts):
//   GET /api/sales/leads/:leadId/preferences          — estado EFETIVO por canal
//   PUT /api/sales/leads/:leadId/preferences/:channel — bloquear/desbloquear um canal
//
// "Um bloqueio ativo sempre vence campanha, fila, agenda automática e
// discador" (doc 26 §8) — o `ContactRestrictionBanner` (features/sales/contacts)
// e o gate de botões de contato do CRM (CrmDetailPage.tsx) leem
// `blockedChannels`/`isFullyBlocked` deste hook, nunca recalculam a regra.
// =============================================================================

import type {
  ContactChannel,
  ContactPreferenceChannelState,
  ContactPreferenceSetBody,
  ContactPreferencesListResponse,
} from '@elemento/shared-schemas';
import {
  CONTACT_CHANNELS,
  ContactPreferenceChannelStateSchema,
  ContactPreferencesListResponseSchema,
} from '@elemento/shared-schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api';

export type { ContactPreferenceChannelState, ContactPreferenceSetBody };

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const contactPreferenceKeys = {
  all: ['sales', 'contact-preferences'] as const,
  lead: (leadId: string) => ['sales', 'contact-preferences', leadId] as const,
};

// ---------------------------------------------------------------------------
// Leitura — GET /api/sales/leads/:leadId/preferences
// ---------------------------------------------------------------------------

async function fetchContactPreferences(leadId: string): Promise<ContactPreferencesListResponse> {
  const raw = await api.get<unknown>(`/api/sales/leads/${encodeURIComponent(leadId)}/preferences`);
  return ContactPreferencesListResponseSchema.parse(raw);
}

/**
 * Deriva o conjunto de canais BLOQUEADOS (estado efetivo) a partir da
 * resposta da API — nunca reimplementada por um consumidor: um canal só
 * aparece aqui quando `state === 'blocked'` (bloqueio expirado já chega como
 * `'allowed'` do servidor, ver header de sales-preferences.ts).
 */
export function computeBlockedChannels(
  preferences: ContactPreferenceChannelState[],
): Set<ContactChannel> {
  return new Set(preferences.filter((p) => p.state === 'blocked').map((p) => p.channel));
}

/**
 * `true` quando TODOS os canais de contato do lead estão bloqueados — só
 * nesse caso os botões de contato (Registrar contato, Agendar retorno) da
 * ficha ficam desabilitados por completo (doc 26 §6.3 "Bloqueio de contato
 * evidente e respeitado por todos os botões"). Um bloqueio parcial (ex:
 * `invalid_number` só bloqueia telefone) mantém os demais canais utilizáveis
 * — o formulário de registro filtra o canal bloqueado individualmente.
 */
export function isFullyBlocked(blockedChannels: Set<ContactChannel>): boolean {
  return CONTACT_CHANNELS.every((channel) => blockedChannels.has(channel));
}

export interface UseContactPreferencesResult {
  preferences: ContactPreferenceChannelState[];
  blockedChannels: Set<ContactChannel>;
  /** Registros com `state === 'blocked'` — para o banner explicar motivo/data. */
  blocked: ContactPreferenceChannelState[];
  isFullyBlocked: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export function useContactPreferences(
  leadId: string,
  opts: { enabled?: boolean } = {},
): UseContactPreferencesResult {
  const enabled = (opts.enabled ?? true) && leadId.length > 0;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: contactPreferenceKeys.lead(leadId),
    queryFn: () => fetchContactPreferences(leadId),
    enabled,
    staleTime: 15_000,
  });

  const preferences = data?.preferences ?? [];
  const blockedChannels = computeBlockedChannels(preferences);

  return {
    preferences,
    blockedChannels,
    blocked: preferences.filter((p) => p.state === 'blocked'),
    isFullyBlocked: enabled ? isFullyBlocked(blockedChannels) : false,
    isLoading: enabled && isLoading,
    isError,
    refetch: () => void refetch(),
  };
}

// ---------------------------------------------------------------------------
// Mutação — PUT /api/sales/leads/:leadId/preferences/:channel
// ---------------------------------------------------------------------------

async function setContactPreference(
  leadId: string,
  channel: ContactChannel,
  body: ContactPreferenceSetBody,
): Promise<ContactPreferenceChannelState> {
  const raw = await api.put<unknown>(
    `/api/sales/leads/${encodeURIComponent(leadId)}/preferences/${encodeURIComponent(channel)}`,
    body,
  );
  return ContactPreferenceChannelStateSchema.parse(raw);
}

export function useSetContactPreference(leadId: string): {
  setAsync: (input: {
    channel: ContactChannel;
    body: ContactPreferenceSetBody;
  }) => Promise<ContactPreferenceChannelState>;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ channel, body }: { channel: ContactChannel; body: ContactPreferenceSetBody }) =>
      setContactPreference(leadId, channel, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contactPreferenceKeys.lead(leadId) });
    },
  });

  return { setAsync: (input) => mutation.mutateAsync(input) };
}
